import { toast } from "react-toastify";
import { BarcodeRecord, LookupOption } from "./types";
import BrotherPrint from "Pages/Printing/BrotherPrint";
import { DymoMultiPrint } from "Pages/Printing/DymoMultiPrint";
import { ZebraMultiPrint } from "Pages/Printing/ZebraMultiPrint";

const getRaw = (record: BarcodeRecord) => record.raw ?? record;

const getPrinterContent = (record: BarcodeRecord, printerLabel?: string) => {
  const raw = getRaw(record);
  const label = `${printerLabel ?? ""}`.toLowerCase();

  if (label.includes("zebra")) {
    return (
      raw?.zpl ??
      raw?.zebra ??
      raw?.zebraContent ??
      raw?.printerContent ??
      raw?.printContent ??
      raw?.content
    );
  }

  if (label.includes("dymo")) {
    return (
      raw?.dymoXml ??
      raw?.dymo ??
      raw?.labelXml ??
      raw?.printerContent ??
      raw?.printContent ??
      raw?.content
    );
  }

  if (label.includes("brother")) {
    return (
      raw?.imageBase64 ??
      raw?.base64 ??
      raw?.brother ??
      raw?.printerContent ??
      raw?.printContent ??
      raw?.content
    );
  }

  return raw?.printerContent ?? raw?.printContent ?? raw?.content;
};

const browserPrint = (records: BarcodeRecord[]) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    toast.error("Please allow popups to print labels");
    return;
  }

  const labelMarkup = records
    .map(
      (record) => `
        <div class="label">
          <div class="barcode-text">${record.value}</div>
          ${record.itemName ? `<div class="muted">${record.itemName}</div>` : ""}
        </div>
      `
    )
    .join("");

  printWindow.document.write(`
    <html>
      <head>
        <title>Print Barcodes</title>
        <style>
          @page { size: auto; margin: 6mm; }
          body { margin: 0; font-family: Arial, sans-serif; color: #000; }
          .label {
            width: 58mm;
            min-height: 28mm;
            border: 1px solid #111;
            padding: 5mm;
            margin: 0 0 4mm;
            box-sizing: border-box;
            page-break-inside: avoid;
          }
          .barcode-text {
            font-family: "Libre Barcode 128", "Courier New", monospace;
            font-size: 18px;
            font-weight: 700;
            letter-spacing: 0;
            word-break: break-all;
          }
          .muted { margin-top: 3mm; font-size: 10px; }
        </style>
      </head>
      <body>${labelMarkup}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

const sendPrinterContent = async (
  content: any[],
  printerLabel: string
): Promise<boolean> => {
  const normalizedPrinterLabel = printerLabel.toLowerCase();

  if (normalizedPrinterLabel.includes("zebra")) {
    await ZebraMultiPrint(content);
    return true;
  }

  if (normalizedPrinterLabel.includes("dymo")) {
    await DymoMultiPrint(content);
    return true;
  }

  if (normalizedPrinterLabel.includes("brother")) {
    await BrotherPrint(content);
    return true;
  }

  return false;
};

export const printSupplyPrinterContent = async (
  content: any[],
  printer?: LookupOption | null
) => {
  const printableContent = Array.isArray(content)
    ? content.filter(Boolean)
    : [];

  if (!printableContent.length) {
    toast.error("No printer content available");
    return;
  }

  const printed = await sendPrinterContent(
    printableContent,
    printer?.label ?? ""
  );

  if (!printed) {
    toast.error("Selected printer is not supported");
  }
};

export const printBarcodeRecords = async (
  records: BarcodeRecord[],
  printer?: LookupOption | null
) => {
  if (!records.length) {
    toast.error("No barcodes available to print");
    return;
  }

  const printerLabel = printer?.label ?? "";
  const content = records
    .map((record) => getPrinterContent(record, printerLabel))
    .filter(Boolean);

  if (content.length && (await sendPrinterContent(content, printerLabel))) {
    return;
  }

  browserPrint(records);
};
